import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Pencil, FileType } from "lucide-react";
import { Button } from "@/components/ui";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

/**
 * Consistent action bar shown at the top of every discipline (derived)
 * plan. Two safe, reusable buttons:
 *   • Open in Editor - jumps to the main /editor with the current model
 *     so the user can freely edit walls / openings / columns and any
 *     downstream plan will pick the change up. Reuses the existing editor
 *     infrastructure; nothing new to break.
 *   • Export PDF - pipes through the existing per-sheet PDF endpoint.
 *
 * Sheets that also expose DXF get a second export button. Everything is
 * opt-in via props so no derived page inherits behaviour it wasn't asked
 * to opt into.
 */
export default function PlanActions({ model, projectName, sheet, sheetMeta = {}, extraDxf = false, dxfEndpoint, dxfBody, testIdPrefix = "plan-actions" }) {
  const { lang } = useAuth();
  const isAr = lang === "ar";
  const navigate = useNavigate();

  const onEdit = () => {
    if (!model) { toast.error(isAr ? "لا يوجد مخطط لتحريره" : "No model to edit"); return; }
    navigate("/editor");
  };
  const onPdf = async () => {
    try {
      await api.editorDownloadSheet(sheet, model, projectName, sheetMeta);
      toast.success(isAr ? "تم تصدير PDF" : "PDF exported");
    } catch (e) {
      toast.error(String(e?.message || e));
    }
  };
  const onDxf = async () => {
    if (!dxfEndpoint) return;
    try {
      await dxfEndpoint(dxfBody || {});
      toast.success(isAr ? "تم تصدير DXF" : "DXF exported");
    } catch (e) {
      toast.error(String(e?.message || e));
    }
  };

  return (
    <div className="mb-3 flex flex-wrap items-center justify-end gap-2" data-testid={testIdPrefix}>
      <Button size="sm" variant="outline" onClick={onEdit} data-testid={`${testIdPrefix}-edit`}>
        <Pencil size={14} /> {isAr ? "افتح في المحرر" : "Open in Editor"}
      </Button>
      <Button size="sm" variant="outline" onClick={onPdf} data-testid={`${testIdPrefix}-pdf`}>
        <FileText size={14} /> {isAr ? "تصدير PDF" : "Export PDF"}
      </Button>
      {extraDxf && dxfEndpoint && (
        <Button size="sm" variant="outline" onClick={onDxf} data-testid={`${testIdPrefix}-dxf`}>
          <FileType size={14} /> {isAr ? "تصدير DXF" : "Export DXF"}
        </Button>
      )}
    </div>
  );
}
